import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatMoney, toAmount, usePagedRows } from "../../../../shared/components/reports";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import {
    subjectName,
    type PractitionerReport,
    type ReportLinks,
    type ReportSessionRow,
} from "../../domain/PractitionerReport";
import MaybeLink from "./MaybeLink";


interface Props {
    report: PractitionerReport;
    locale: string;
    groupOf: (count: number) => string;
    links: ReportLinks;
}

const CARD = "rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]";
const HEADING = "py-2 pr-4 font-medium";
const CELL = "py-2.5 pr-4 text-content-primary";

/** `YYYY-MM-DDTHH:MM:SS` as the calendar day and time it names, wherever the browser is. */
function wallClock(value: string, locale: string, withTime = true): string {
    const [day, time = ""] = value.split("T");
    const [year, month, date] = day.split("-").map(Number);
    const [hours = 0, minutes = 0] = time.split(":").map(Number);
    const moment = new Date(year, month - 1, date, hours, minutes);
    return moment.toLocaleString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
}

/**
 * Every session of the window, one by one: those held or still to come, and
 * — apart — those called off, with who asked and when, at 0 whoever asked.
 * A cancelled session that is rescheduled leaves the second table for the
 * first, on its new date.
 */
export default function SessionDetailTables({ report, locale, groupOf, links }: Props) {
    const { t } = useTranslation();
    const held = useMemo(() => report.sessions.filter((s) => s.status !== "cancelled"), [report.sessions]);
    const cancelled = useMemo(() => report.sessions.filter((s) => s.status === "cancelled"), [report.sessions]);
    // A training centre's mentorships always show course and module — "—"
    // when none was set — so a cancelled one can be matched to a later one.
    const withCourse = report.training || report.sessions.some((s) => s.course);
    const withModule = report.training || report.sessions.some((s) => s.module);
    const withMentor = report.mentors.length > 0;
    const money = (value: string | null) =>
        value === null ? t("reports.table.noRate") : formatMoney(toAmount(value), locale);

    const leadHeadings = (
        <>
            <th scope="col" className={HEADING}>{t("reports.sessions.date")}</th>
            {withMentor && <th scope="col" className={HEADING}>{t("reports.mentors.name")}</th>}
            <th scope="col" className={HEADING}>{t("reports.table.subject")}</th>
            {withCourse && <th scope="col" className={HEADING}>{t("reports.table.course")}</th>}
            {withModule && <th scope="col" className={HEADING}>{t("reports.sessions.module")}</th>}
            <th scope="col" className={HEADING}>{t("reports.table.type")}</th>
        </>
    );
    const leadCells = (session: ReportSessionRow) => (
        <>
            <td className={`${CELL} whitespace-nowrap tabular-nums`}>
                <MaybeLink to={links.session(session)}>{wallClock(session.date, locale)}</MaybeLink>
            </td>
            {withMentor && (
                <td className={CELL}>
                    <MaybeLink to={session.therapist_id ? links.mentor(session.therapist_id) : null}>
                        {session.therapist_name ?? "—"}
                    </MaybeLink>
                </td>
            )}
            <th scope="row" className={`${CELL} font-medium`}>
                <MaybeLink to={links.subject(session.subject_type, session.subject_id)}>
                    {subjectName(session, groupOf)}
                </MaybeLink>
            </th>
            {withCourse && <td className={CELL}>{session.course ?? "—"}</td>}
            {withModule && <td className={CELL}>{session.module ?? "—"}</td>}
            <td className={CELL}>{t(`reports.type.${session.type}`)}</td>
        </>
    );
    // "3/5": who came, of how many. A held group nobody took attendance for
    // says so; a session nobody could have come to has a dash.
    const attendance = (session: ReportSessionRow) => {
        if (session.attendees !== null && session.participants) {
            return `${session.attendees}/${session.participants}`;
        }
        if (session.status === "finished" && session.subject_type === "group") {
            return t("reports.sessions.attendanceUnknown");
        }
        return "—";
    };
    const requester = (session: ReportSessionRow) =>
        session.cancelled_by === "patient"
            ? t("reports.sessions.byPatient")
            : session.cancelled_by === "therapist"
              ? t("reports.sessions.byTherapist")
              : t("reports.sessions.byUnknown");

    return (
        <>
            <section className={CARD}>
                <h2 className="mb-4 text-base font-semibold text-content-primary">{t("reports.sessions.title")}</h2>
                {held.length === 0 ? (
                    <p className="rounded-xl bg-surface-sunken p-6 text-center text-sm text-content-tertiary">
                        {t("reports.sessions.none")}
                    </p>
                ) : (
                    <Rows
                        rows={held}
                        header={
                            <tr>
                                {leadHeadings}
                                <th scope="col" className={HEADING}>{t("reports.sessions.status")}</th>
                                <th scope="col" className={`${HEADING} text-right`}>{t("reports.sessions.attendees")}</th>
                                {report.has_amounts && (
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.table.amount")}</th>
                                )}
                            </tr>
                        }
                        render={(session) => (
                            <tr key={session.id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                {leadCells(session)}
                                <td className={CELL}>{t(`reports.sessions.statusOptions.${session.status}`)}</td>
                                <td className={`${CELL} text-right tabular-nums`}>{attendance(session)}</td>
                                {report.has_amounts && (
                                    <td className={`${CELL} text-right tabular-nums`}>
                                        {session.amount === null ? "—" : money(session.amount)}
                                    </td>
                                )}
                            </tr>
                        )}
                    />
                )}
            </section>

            {cancelled.length > 0 && (
                <section className={CARD}>
                    <h2 className="text-base font-semibold text-content-primary">
                        {t("reports.sessions.cancelledTitle")}
                    </h2>
                    <p className="mb-4 mt-1 text-sm text-content-secondary">{t("reports.sessions.cancelledHint")}</p>
                    <Rows
                        rows={cancelled}
                        header={
                            <tr>
                                {leadHeadings}
                                <th scope="col" className={HEADING}>{t("reports.sessions.cancelledBy")}</th>
                                <th scope="col" className={HEADING}>{t("reports.sessions.cancelledOn")}</th>
                                {report.has_amounts && (
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.table.amount")}</th>
                                )}
                            </tr>
                        }
                        render={(session) => (
                            <tr key={session.id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                {leadCells(session)}
                                <td className={CELL}>{requester(session)}</td>
                                <td className={`${CELL} tabular-nums`}>
                                    {session.cancelled_at ? wallClock(session.cancelled_at, locale, false) : "—"}
                                </td>
                                {/* Whoever asked, a cancelled session is worth nothing. */}
                                {report.has_amounts && (
                                    <td className={`${CELL} text-right tabular-nums`}>{formatMoney(0, locale)}</td>
                                )}
                            </tr>
                        )}
                    />
                </section>
            )}
        </>
    );
}

function Rows({
    rows,
    header,
    render,
}: {
    rows: ReportSessionRow[];
    header: React.ReactNode;
    render: (row: ReportSessionRow) => React.ReactNode;
}) {
    const paged = usePagedRows(rows);

    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                    <thead className="border-b border-gray-200 text-xs uppercase tracking-wide text-content-tertiary dark:border-gray-800">
                        {header}
                    </thead>
                    <tbody>{paged.page.map(render)}</tbody>
                </table>
            </div>
            <Pagination
                offset={paged.offset}
                total={paged.total}
                pageSize={paged.pageSize}
                onPrev={paged.onPrev}
                onNext={paged.onNext}
            />
        </>
    );
}
